
import React, { useState, useEffect } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import WebsiteChatModal from './WebsiteChatModal';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";

const FloatingChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    // Show the bubble with a slight delay for smooth entrance
    const visibilityTimer = setTimeout(() => {
      setIsVisible(true);
    }, 1000);
    
    // Show the tooltip after a delay
    const tooltipTimer = setTimeout(() => {
      if (!isOpen) {
        setShowTooltip(true);
      }
    }, 3000);
    
    return () => {
      clearTimeout(visibilityTimer);
      clearTimeout(tooltipTimer);
    };
  }, [isOpen]);

  // Auto-hide tooltip after some time but show it again periodically
  useEffect(() => {
    if (showTooltip && !isOpen) {
      const hideTimer = setTimeout(() => {
        setShowTooltip(false);
      }, 6000);
      
      // Show again after a longer interval
      const reshowTimer = setTimeout(() => {
        if (!isOpen) {
          setShowTooltip(true);
        }
      }, 15000);
      
      return () => {
        clearTimeout(hideTimer);
        clearTimeout(reshowTimer);
      };
    }
  }, [showTooltip, isOpen]);

  // Handle ESC key for accessibility
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isOpen]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setShowTooltip(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer open={isOpen} onOpenChange={setIsOpen}>
          <DrawerContent className="h-[85vh]">
            <DrawerHeader className="border-b">
              <div className="flex items-center justify-between">
                <DrawerTitle className="text-lg font-semibold">
                  ¿Cómo podemos ayudarte?
                </DrawerTitle>
                <DrawerClose asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <X className="h-4 w-4" />
                  </Button>
                </DrawerClose>
              </div>
            </DrawerHeader>
            <div className="flex-1 p-4">
              <WebsiteChatModal onClose={() => setIsOpen(false)} />
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {/* Floating Widget */}
      <div className="fixed bottom-20 right-6 z-40">
        {!isMobile && isOpen ? (
          <WebsiteChatModal onClose={() => setIsOpen(false)} />
        ) : (
          <div className="relative group">
            {/* Tooltip */}
            <div 
              className={cn(
                "absolute -top-16 right-0 bg-white text-navy px-4 py-3 rounded-xl shadow-xl",
                "text-sm font-medium whitespace-nowrap transform transition-all duration-500 ease-out",
                "before:absolute before:bottom-[-8px] before:right-4 before:w-4 before:h-4 before:bg-white before:rotate-45 before:shadow-lg",
                "border border-gray-100/50",
                showTooltip 
                  ? "opacity-100 translate-y-0 scale-100" 
                  : "opacity-0 translate-y-2 scale-95 pointer-events-none"
              )}
            >
              <span className="text-primary font-medium">¿Tienes dudas?</span>
            </div>
            
            {/* Main chat button */}
            <Button
              onClick={toggleChat}
              className={cn(
                "h-14 w-14 rounded-full shadow-lg bg-primary hover:bg-primary/90 text-white",
                "flex items-center justify-center transition-all duration-300 ease-out",
                "hover:scale-110 hover:shadow-xl focus:ring-4 focus:ring-primary/20 focus:ring-offset-2",
                "border-2 border-white/10"
              )}
              aria-label="Abrir chat de ayuda"
              aria-expanded={isOpen}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setTimeout(() => setShowTooltip(false), 1000)}
            >
              <MessageSquare className="h-6 w-6" strokeWidth={2} />
            </Button>
            
            {/* Subtle glow effect */}
            <div className="absolute -inset-2 rounded-full bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none blur-sm"></div>
          </div>
        )}
      </div>
    </>
  );
};

export default FloatingChatBubble;
